import React, { useState, useEffect } from 'react';
import { get, find } from 'lodash';
import { getCountryId } from '@fff-web/fff-utilities';
import { COUNTRY_SELECTION } from '@fff-web/fff-utilities';
import {
  updateAddress,
  addAddress as addAddressService,
  removeAddress as removeAddressService,
  verifyAddress as verifyAddressService,
} from '@fff-web/fff-utilities';

import Modal from '../Modal';
import AddressForm from '../AddressForm';
import { Times } from '../Icon';
import AddressVerificationModal from '../AddressVerificationModal';
import AddressList from '../AddressList';
import Toast, { showErrorToast } from '../Toast';
import AddAddressButton from '../AddAddressButton';
import styles from './Address.module.scss';

type AddressProps = {
  addressesData: any[];
  selectedAddressId?: string;
  setSelectedAddressId?: (id: string) => void;
  showRadioSelectors?: boolean;
  layoutType: string;
};

export const ADDRESS_LAYOUT_TYPES = {
  checkout: 'CHECKOUT',
  userAccount: 'USER_ACCOUNT',
};

const ADDRESS_MODAL_NAMES = {
  edit: 'EDIT_ADDRESS_MODAL',
  add: 'ADD_ADDRESS_MODAL',
};

const ADDRESS_MODAL_TITLE = {
  [ADDRESS_MODAL_NAMES.edit]: 'Edit Address',
  [ADDRESS_MODAL_NAMES.add]: 'Add New Address',
};

const handleAddressValidation = (deliveryStatus: string, fffUndeliverable: boolean) => {
  if (deliveryStatus === 'deliverable') {
    if (fffUndeliverable) return 'suggested';
    return 'success';
  }
  return 'undeliverable';
};

const Address: React.FC<AddressProps> = ({
  addressesData,
  selectedAddressId,
  setSelectedAddressId,
  layoutType,
}) => {
  const [addresses, setAddresses] = useState<any[]>([]);
  const [defaultAddressId, setDefaultAddressId] = useState<string>('');
  const [currentAddressId, setCurrentAddressId] = useState<string | null>(null);
  const [currentModal, setCurrentModal] = useState<string>('');
  const [isOpenAddressModal, setIsOpenAddressModal] = useState<boolean>(false);
  const [verifyingAddress, setVerifyingAddress] = useState<boolean>(false);
  const [verificationModalOpen, setVerificationModalOpen] = useState<any>('');
  const [verificationModalData, setVerificationModalData] = useState<{
    submitted: any;
    verified: any;
  }>({
    submitted: {},
    verified: {},
  });
  const [payload, setPayload] = useState<any>(null);
  const [stateList, setStateList] = useState<any[]>([]);

  const currentAddress =
    addresses.find((address) => address.userAddress.id === currentAddressId)?.userAddress || {};

  const {
    firstName,
    lastName,
    contactNumber,
    addressLine1,
    addressLine2,
    city,
    state,
    zipcode,
    country,
    isDefault,
  } = currentAddress;
  const defaultAddressIndex = addresses?.findIndex((item) => item.userAddress.isDefault) ?? -1;
  const defaultCountry = addresses?.[defaultAddressIndex]?.userAddress?.country || 'US';
  const formCountry = country || defaultCountry;

  const handleCloseAddressModal = () => setIsOpenAddressModal(false);
  const handleOpenAddressModal = (address: any, modalName: string) => {
    if (address.isDefault && address.id !== defaultAddressId) {
      address.isDefault = false;
    }

    setCurrentAddressId(address.id);
    setCurrentModal(modalName);

    setIsOpenAddressModal(true);
  };

  const handleEditAddress = (address: any) => {
    handleOpenAddressModal(address, ADDRESS_MODAL_NAMES.edit);
  };

  const handleOnClickAddAddressButton = () => handleOpenAddressModal({}, ADDRESS_MODAL_NAMES.add);

  const handleError = (error: any = undefined) => {
    if (error) {
      if (error && error.errors) {
        error = JSON.parse(error);
        showErrorToast(error.errors[0]);
      } else {
        showErrorToast(error);
      }
    } else {
      showErrorToast('Unexpected error');
    }
  };

  const editAddress = async (payloadToSubmit: any) => {
    try {
      const addressUpdated = await updateAddress(payloadToSubmit, currentAddress.id);
      setAddresses((prevAddresses) =>
        prevAddresses.map((address) => {
          if (addressUpdated.isDefault && address.userAddress.id !== addressUpdated.id) {
            return {
              ...address,
              userAddress: {
                ...address.userAddress,
                isDefault: false,
              },
            };
          }
          return address.userAddress.id === addressUpdated.id
            ? { ...address, userAddress: addressUpdated }
            : address;
        }),
      );
      handleCloseAddressModal();
      closeVerificationModal();
      if (setSelectedAddressId) {
        setSelectedAddressId(addressUpdated.id);
      }
      if (addressUpdated.isDefault) {
        setDefaultAddressId(addressUpdated.id);
      }
    } catch (error) {
      handleError(error);
    }
  };

  const addAddress = async (payloadToSubmit: any) => {
    try {
      const addressAddedId = await addAddressService(payloadToSubmit);
      setAddresses((prevAddresses) => {
        if (payloadToSubmit.isDefault) {
          const updatedAddresses = prevAddresses.map((address) => ({
            ...address,
            userAddress: {
              ...address.userAddress,
              isDefault: address.userAddress.id === addressAddedId ? true : false,
            },
          }));

          return [
            ...updatedAddresses,
            {
              userAddress: {
                id: addressAddedId,
                ...payloadToSubmit,
                zipcode: payloadToSubmit.zipCode,
              },
            },
          ];
        } else {
          return [
            ...prevAddresses,
            {
              userAddress: {
                id: addressAddedId,
                ...payloadToSubmit,
                zipcode: payloadToSubmit.zipCode,
              },
            },
          ];
        }
      });
      handleCloseAddressModal();
      closeVerificationModal();
      if (setSelectedAddressId) {
        setSelectedAddressId(addressAddedId);
      }
      if (payloadToSubmit.isDefault) {
        setDefaultAddressId(addressAddedId);
      }
    } catch (error) {
      handleError(error);
    }
  };

  const removeAddress = async (addressId: string) => {
    try {
      await removeAddressService(addressId);
      setAddresses(addresses.filter((address) => address.userAddress.id !== addressId));
    } catch (error) {
      handleError(error);
    }
  };

  const verifyAddress = async (payloadToSubmit: any, methodHandler: (payload: any) => void) => {
    const { city, state, addressLine1, addressLine2, country, zipCode, zipcode } = payloadToSubmit;

    const verifyAddressPayload = {
      city,
      state,
      countryCode: country,
      primaryLine: addressLine1,
      secondaryLine: addressLine2,
      zip: zipCode | zipcode,
    };

    try {
      const validationResponse = await verifyAddressService(verifyAddressPayload);
      setVerifyingAddress(true);
      if (validationResponse.success) {
        const { deliveryStatus, submitted } = validationResponse;
        const { fffUndeliverable } = submitted;
        const validation = handleAddressValidation(deliveryStatus, fffUndeliverable);
        setVerifyingAddress(false);
        switch (validation) {
          case 'success': {
            methodHandler(payloadToSubmit);
            break;
          }
          case 'suggested':
            setVerificationModalOpen('suggested');
            setVerificationModalData(validationResponse);
            break;
          default:
            // undeliverable
            setVerificationModalOpen('undeliverable');
            setVerificationModalData(validationResponse);
        }
      }
    } catch (error) {
      setVerifyingAddress(false);
      showErrorToast(
        'Address Change Unsuccessful',
        "We're experiencing some technical difficulties. Please try again later.",
      );
    }
  };

  const closeVerificationModal = () => {
    setVerificationModalOpen('');
    setVerificationModalData({
      submitted: {},
      verified: {},
    });
  };

  const handleClickUseThisAddress = async (
    suggestedSelected: boolean,
    suggestedData: any,
    resetSuggestedState: () => void,
  ) => {
    const data = suggestedSelected ? { ...payload, ...suggestedData } : payload;
    if (currentModal === ADDRESS_MODAL_NAMES.edit) {
      await editAddress(data);
    } else if (currentModal === ADDRESS_MODAL_NAMES.add) {
      await addAddress(data);
    }
    resetSuggestedState();
  };

  useEffect(() => {
    if (addressesData) {
      setAddresses(addressesData);
      const currentDefaultAddress = addressesData.find((address) => address.userAddress.isDefault);

      if (currentDefaultAddress) {
        const {
          userAddress: { id },
        } = currentDefaultAddress;
        if (setSelectedAddressId) {
          setSelectedAddressId(id);
        }
        setDefaultAddressId(id);
      }
    }
  }, [addressesData]);

  const submitAddress = (formData: any) => {
    const {
      address1,
      address2,
      city,
      firstName,
      lastName,
      phone,
      country,
      region,
      zipcode,
      isDefault,
    } = formData;

    const payloadToSubmit = {
      addressLine1: address1,
      addressLine2: address2,
      city,
      state: get(find(stateList, ['id', Number(region)]), 'stateCode'),
      zipCode: zipcode,
      country: get(COUNTRY_SELECTION, [country, 'code']),
      contactNumber: phone,
      firstName,
      lastName,
      isDefault: currentAddress.isDefault || isDefault,
    };

    setPayload(payloadToSubmit);
    if (currentModal === ADDRESS_MODAL_NAMES.edit) {
      verifyAddress(payloadToSubmit, editAddress);
    } else if (currentModal === ADDRESS_MODAL_NAMES.add) {
      verifyAddress(payloadToSubmit, addAddress);
    }
  };

  if (addresses.length === 0) {
    return null;
  }

  return (
    <>
      <Toast />
      <div className={styles.container}>
        <AddressList
          addresses={addresses}
          selectedAddressId={selectedAddressId}
          setSelectedAddressId={setSelectedAddressId}
          handleEditAddress={handleEditAddress}
          handleRemoveAddress={removeAddress}
          layoutType={layoutType}
        />
        <AddAddressButton
          showMaxAddressLabel={addresses.length > 9}
          handleClick={handleOnClickAddAddressButton}
        />
      </div>
      <Modal isOpen={isOpenAddressModal} className={styles.modal}>
        <div className={styles.addressForm}>
          <div className={styles.header}>
            <h1>{ADDRESS_MODAL_TITLE[currentModal]}</h1>
            <Times onClick={handleCloseAddressModal} />
          </div>
          {currentAddress.isDefault && <h2>Primary Shipping Address</h2>}
          <AddressForm
            firstName={firstName}
            lastName={lastName}
            address1={addressLine1}
            address2={addressLine2}
            city={city}
            region={state}
            country={getCountryId(formCountry)}
            zipcode={zipcode || currentAddress.zipCode}
            phone={contactNumber}
            isDefault={isDefault}
            submitButtonName={
              layoutType === ADDRESS_LAYOUT_TYPES.userAccount ? 'SAVE' : 'USE THIS ADDRESS'
            }
            onSubmit={submitAddress}
            cancelButtonName="CANCEL"
            onCancel={handleCloseAddressModal}
            getStateList={setStateList}
            showPhoneNumber
          />
        </div>
      </Modal>
      <AddressVerificationModal
        modal={verificationModalOpen}
        data={verificationModalData}
        loading={verifyingAddress}
        closeModal={closeVerificationModal}
        payload={payload}
        onClickUseThisAddress={handleClickUseThisAddress}
        onClickUseAsEntered={currentModal === ADDRESS_MODAL_NAMES.edit ? editAddress : addAddress}
      />
    </>
  );
};

export default Address;
